import axios from 'axios';
import { RepositoryInfo, FileAnalysis, ProjectAnalysis, FolderNode } from '../types';
import { SecurityAnalysisService } from './securityAnalysis';
import { getAISecurityInsights, getAIComplianceInsights } from './openRouterService';


const GITHUB_API_BASE = 'https://api.github.com';

// Performance configuration based on project size
const PERFORMANCE_CONFIG = {
  SMALL: { maxFiles: 100, maxDepth: 3, maxContentFiles: 30, parallelRequests: 3 },
  MEDIUM: { maxFiles: 500, maxDepth: 4, maxContentFiles: 50, parallelRequests: 5 },
  LARGE: { maxFiles: 1000, maxDepth: 5, maxContentFiles: 80, parallelRequests: 8 },
  HUGE: { maxFiles: 2000, maxDepth: 6, maxContentFiles: 150, parallelRequests: 10 }
};

// Cache for API responses
const apiCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class GitHubApiService {
  private token: string | null = null;
  private progressCallback?: (progress: number, message: string) => void;

  setToken(token: string) {
    this.token = token;
  }

  setProgressCallback(callback: (progress: number, message: string) => void) {
    this.progressCallback = callback;
  }

  // Clear API cache to force fresh data
  clearCache() {
    apiCache.clear();
    console.log('API cache cleared');
  }

  // Get cache statistics
  getCacheStats() {
    return {
      size: apiCache.size,
      entries: Array.from(apiCache.keys()).slice(0, 10), // Show first 10 entries
      timestamp: new Date().toISOString()
    };
  }

  private updateProgress(progress: number, message: string) {
    if (this.progressCallback) {
      this.progressCallback(progress, message);
    }
  }

  private getHeaders() {
    const headers: Record<string, string> = {
      'Accept': 'application/vnd.github.v3+json',
    };
    
    if (this.token) {
      headers['Authorization'] = `token ${this.token}`;
    }
    
    return headers;
  }

  private extractRepoInfo(url: string): { owner: string; repo: string } | null {
    const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      return {
        owner: match[1],
        repo: match[2].replace('.git', '').replace('#', ''),
      };
    }
    return null;
  }

  private async cachedApiCall<T>(url: string): Promise<T> {
    const cacheKey = `${url}_${this.token || 'anonymous'}`;
    const cached = apiCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(url, { headers: this.getHeaders() });
      apiCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Rate limit exceeded. Please add a GitHub token for higher limits or try again later.');
      } else if (error.response?.status === 404) {
        throw new Error('Repository not found. Please check the URL and ensure the repository is public.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please check your GitHub token.');
      } else {
        throw new Error(`GitHub API error: ${error.response?.status || 'Unknown error'}`);
      }
    }
  }

  async getRepositoryInfo(url: string): Promise<RepositoryInfo> {
    const repoInfo = this.extractRepoInfo(url);
    if (!repoInfo) {
      throw new Error('Invalid GitHub URL');
    }

    return await this.cachedApiCall<RepositoryInfo>(
      `${GITHUB_API_BASE}/repos/${repoInfo.owner}/${repoInfo.repo}`
    );
  }

  async getRepositoryContents(url: string, path: string = ''): Promise<FileAnalysis[]> {
    const repoInfo = this.extractRepoInfo(url);
    if (!repoInfo) {
      throw new Error('Invalid GitHub URL');
    }

    const data = await this.cachedApiCall<any[]>(
      `${GITHUB_API_BASE}/repos/${repoInfo.owner}/${repoInfo.repo}/contents/${path}`
    );

    return data.map((item: any) => ({
      path: item.path,
      type: item.type,
      size: item.size,
      language: this.getLanguageFromPath(item.path),
    }));
  }

  async getFileContent(url: string, path: string): Promise<string> {
    const repoInfo = this.extractRepoInfo(url);
    if (!repoInfo) {
      throw new Error('Invalid GitHub URL');
    }

    const data = await this.cachedApiCall<any>(
      `${GITHUB_API_BASE}/repos/${repoInfo.owner}/${repoInfo.repo}/contents/${path}`
    );

    return atob(data.content);
  }

  private getLanguageFromPath(path: string): string {
    const extension = path.split('.').pop()?.toLowerCase();
    const languageMap: { [key: string]: string } = {
      'js': 'JavaScript',
      'ts': 'TypeScript',
      'jsx': 'React',
      'tsx': 'React TypeScript',
      'py': 'Python',
      'java': 'Java',
      'cpp': 'C++',
      'c': 'C',
      'go': 'Go',
      'rs': 'Rust',
      'php': 'PHP',
      'rb': 'Ruby',
      'swift': 'Swift',
      'kt': 'Kotlin',
      'scala': 'Scala',
      'html': 'HTML',
      'css': 'CSS',
      'scss': 'SCSS',
      'sass': 'Sass',
      'json': 'JSON',
      'yaml': 'YAML',
      'yml': 'YAML',
      'md': 'Markdown',
      'txt': 'Text',
      'sh': 'Shell',
      'bash': 'Bash',
      'dockerfile': 'Docker',
      'sql': 'SQL',
    };

    return languageMap[extension || ''] || 'Unknown';
  }

  // Determine project size and get appropriate performance config
  private getPerformanceConfig(repoInfo: RepositoryInfo): typeof PERFORMANCE_CONFIG.SMALL {
    const size = repoInfo.size || 0;
    const stars = repoInfo.stargazers_count || 0;
    const forks = repoInfo.forks_count || 0;
    
    // Determine size based on repository metrics
    if (size > 100000 || stars > 1000 || forks > 500) {
      return PERFORMANCE_CONFIG.HUGE;
    } else if (size > 50000 || stars > 500 || forks > 200) {
      return PERFORMANCE_CONFIG.LARGE;
    } else if (size > 10000 || stars > 100 || forks > 50) {
      return PERFORMANCE_CONFIG.MEDIUM;
    } else {
      return PERFORMANCE_CONFIG.SMALL;
    }
  }

  // Optimized recursive fetch with depth limits and file counting
  private async fetchAllContents(url: string, path: string = '', depth: number = 0, config: typeof PERFORMANCE_CONFIG.SMALL): Promise<FileAnalysis[]> {
    if (depth > config.maxDepth) {
      return [];
    }

    const contents = await this.getRepositoryContents(url, path);
    let all = [...contents];
    
    // Update progress
    this.updateProgress(
      Math.min((depth / config.maxDepth) * 30, 30),
      `Scanning directory: ${path || 'root'} (${all.length} items found)`
    );

    // Process directories in parallel with concurrency limit
    const directories = contents.filter(item => item.type === 'dir');
    const chunks = this.chunkArray(directories, config.parallelRequests);
    
    for (const chunk of chunks) {
      const promises = chunk.map(item => 
        this.fetchAllContents(url, item.path, depth + 1, config)
      );
      
      const results = await Promise.all(promises);
      all = all.concat(...results);
      
      // Check if we've exceeded the file limit
      if (all.length > config.maxFiles) {
        console.warn(`File limit exceeded (${config.maxFiles}), stopping scan at ${all.length} files`);
        break;
      }
    }

    return all.slice(0, config.maxFiles);
  }

  // Helper to chunk array for parallel processing
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // Parallel file content fetching with rate limiting
  private async getFilesWithContentParallel(url: string, contents: FileAnalysis[], config: typeof PERFORMANCE_CONFIG.SMALL): Promise<FileAnalysis[]> {
    const filesWithContent: FileAnalysis[] = [];
    
    // Filter for important files first - expanded to include more code files
    const keyFilePatterns = [
      'package.json', 'package-lock.json', 'yarn.lock',
      '.env', '.env.example', '.env.local',
      'dockerfile', 'docker-compose',
      'requirements.txt', 'pom.xml', 'build.gradle',
      '.gitignore', '.eslintrc', '.prettierrc',
      'webpack.config', 'vite.config', 'rollup.config',
      'tsconfig.json', 'jsconfig.json',
      'README', 'CONTRIBUTING', 'LICENSE',
      'security', 'auth', 'config',
      // Add more code file patterns for security analysis
      '.js', '.ts', '.jsx', '.tsx', '.py', '.java', '.go', '.rs', '.php', '.rb',
      '.vue', '.svelte', '.astro', '.html', '.css', '.scss', '.sql',
      'app.js', 'index.js', 'main.js', 'server.js', 'app.py', 'main.py',
      'controller', 'service', 'model', 'route', 'api', 'handler'
    ];

    const importantFiles = contents.filter(file => 
      file.type === 'file' && (
        keyFilePatterns.some(pattern => file.path.toLowerCase().includes(pattern.toLowerCase())) ||
        (file.size && file.size < 100000)
      )
    ).slice(0, config.maxContentFiles);

    console.log('🔍 Files with content analysis:', {
      totalFiles: contents.length,
      importantFilesCount: importantFiles.length,
      maxContentFiles: config.maxContentFiles,
      sampleFiles: importantFiles.slice(0, 5).map(f => f.path)
    });

    this.updateProgress(40, `Fetching content for ${importantFiles.length} important files`);

    // Process files in parallel chunks
    const chunks = this.chunkArray(importantFiles, config.parallelRequests);
    
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const promises = chunk.map(async (file) => {
        try {
          const content = await this.getFileContent(url, file.path);
          return { ...file, content };
        } catch (error) {
          console.warn(`Could not fetch content for ${file.path}:`, error);
          return file;
        }
      });

      const results = await Promise.all(promises);
      filesWithContent.push(...results);

      // Update progress
      const progress = 40 + ((i + 1) / chunks.length) * 20;
      this.updateProgress(progress, `Processed ${filesWithContent.length}/${importantFiles.length} files`);
    }

    // Add remaining files without content
    const remainingFiles = contents.filter(file => 
      !importantFiles.some(imp => imp.path === file.path)
    );
    filesWithContent.push(...remainingFiles);

    return filesWithContent;
  }

  async analyzeRepository(url: string): Promise<ProjectAnalysis> {
    const startTime = Date.now();
    
    try {
      this.updateProgress(0, 'Starting repository analysis...');
      
      const repoInfo = await this.getRepositoryInfo(url);
      this.updateProgress(5, 'Repository info fetched, determining analysis scope...');
      
      // Get performance configuration based on repository size
      const config = this.getPerformanceConfig(repoInfo);
      console.log(`Repository size: ${repoInfo.size}, using ${Object.keys(PERFORMANCE_CONFIG).find(key => PERFORMANCE_CONFIG[key as keyof typeof PERFORMANCE_CONFIG] === config)} config`);
      
      // Fetch contents with optimized limits
      const contents = await this.fetchAllContents(url, '', 0, config);
      
      this.updateProgress(35, `Analyzing ${contents.length} files and folders...`);
      
      // Analyze documentation (lightweight)
      const documentation = await this.analyzeDocumentation(url, contents);
      this.updateProgress(50, 'Documentation analysis completed...');
      
      // Analyze architecture (lightweight)
      const architecture = await this.analyzeArchitecture(url, contents);
      this.updateProgress(60, 'Architecture analysis completed...');
      
      // Analyze best practices (lightweight)
      const bestPractices = await this.analyzeBestPractices(url, contents);
      this.updateProgress(70, 'Best practices analysis completed...');
      
      // Analyze errors (lightweight)
      const errors = await this.analyzeErrors(url, contents);
      this.updateProgress(75, 'Error analysis completed...');
      
      // Get files with content for security analysis (heavy operation)
      const filesWithContent = await this.getFilesWithContentParallel(url, contents, config);
      this.updateProgress(85, 'Security analysis in progress...');
      
      console.log('🔒 Security analysis input:', {
        filesWithContentCount: filesWithContent.length,
        filesWithActualContent: filesWithContent.filter(f => f.content).length,
        sampleFilesWithContent: filesWithContent.filter(f => f.content).slice(0, 3).map(f => ({
          path: f.path,
          contentLength: f.content?.length || 0
        }))
      });
      
      // Analyze security
      const security = await SecurityAnalysisService.analyzeSecurity(
        repoInfo,
        architecture.dependencies || {},
        filesWithContent || []
      );
      
      this.updateProgress(93, 'Generating summary...');

      // Generate rule-based summary
      const summary = this.generateSummary(documentation, architecture, bestPractices, errors, security);

      // AI-powered security insights via OpenRouter
      this.updateProgress(96, 'Running AI security analysis...');
      let aiInsights = null;
      let aiComplianceInsights = null;
      try {
        [aiInsights, aiComplianceInsights] = await Promise.all([
          getAISecurityInsights({
            repoName: repoInfo.full_name,
            language: repoInfo.language || '',
            topics: repoInfo.topics || [],
            overallScore: security.overallScore,
            avsvLevel: security.avsvLevel,
            secretsInCode: security.secretsManagement.secretsInCode,
            dependencyVulnsCount: security.dependencySecurity.vulnerableDependencies,
            vibeCodeRiskScore: security.vibeCodeAnalysis.vibeCodeRiskScore,
            aiLibrariesDetected: security.vibeCodeAnalysis.aiLibrariesDetected,
            vulnerabilities: security.vulnerabilities.map(v => ({
              severity: v.severity,
              title: v.title,
              category: v.category,
              owaspCategory: v.owaspCategory,
            })),
          }),
          getAIComplianceInsights({
            repoName: repoInfo.full_name,
            language: repoInfo.language || '',
            securityScore: security.overallScore,
            gdprCompliant: security.compliance.gdprCompliant,
            soxCompliant: security.compliance.soxCompliant,
            hipaaCompliant: security.compliance.hipaaCompliant,
            pciCompliant: security.compliance.pciCompliant,
            complianceIssues: security.compliance.complianceIssues,
            secretsInCode: security.secretsManagement.secretsInCode,
            hasAuth: bestPractices.bestPractices.securityPractices.hasAuth,
            hasLogging: bestPractices.bestPractices.errorHandling.hasLogging,
            hasEncryption: security.configurationSecurity.hasEncryption,
          }),
        ]);
      } catch (aiError) {
        console.warn('AI insights unavailable, using rule-based summary:', aiError);
      }

      const totalTime = Date.now() - startTime;
      console.log(`Analysis completed in ${totalTime}ms for ${contents.length} files`);

      this.updateProgress(100, 'Analysis completed!');

      return {
        repository: repoInfo,
        documentation,
        architecture,
        bestPractices,
        security,
        errors,
        summary,
        aiInsights,
        aiComplianceInsights,
      };
    } catch (error: any) {
      // Provide more specific error messages
      if (error.message.includes('Rate limit exceeded')) {
        throw new Error('Rate limit exceeded. Please add a GitHub token for higher limits or try again later. You can create a token at https://github.com/settings/tokens');
      } else if (error.message.includes('Repository not found')) {
        throw new Error('Repository not found. Please check the URL and ensure the repository is public.');
      } else if (error.message.includes('Authentication failed')) {
        throw new Error('Authentication failed. Please check your GitHub token.');
      } else if (error.message.includes('Invalid GitHub URL')) {
        throw new Error('Invalid GitHub URL. Please enter a valid GitHub repository URL (e.g., https://github.com/username/repository)');
      } else {
        throw new Error(`Failed to analyze repository: ${error.message}`);
      }
    }
  }

  private async analyzeDocumentation(url: string, contents: FileAnalysis[]) {
    const readmeFiles = contents.filter(file => 
      file.path.toLowerCase().includes('readme') && file.type === 'file'
    );
    
    const contributingFiles = contents.filter(file => 
      file.path.toLowerCase().includes('contributing') && file.type === 'file'
    );
    
    const licenseFiles = contents.filter(file => 
      file.path.toLowerCase().includes('license') && file.type === 'file'
    );

    let readmeContent = '';
    let contributingContent = '';
    let licenseContent = '';

    if (readmeFiles.length > 0) {
      try {
        readmeContent = await this.getFileContent(url, readmeFiles[0].path);
      } catch (error) {
        console.warn('Could not fetch README content');
      }
    }

    if (contributingFiles.length > 0) {
      try {
        contributingContent = await this.getFileContent(url, contributingFiles[0].path);
      } catch (error) {
        console.warn('Could not fetch CONTRIBUTING content');
      }
    }

    if (licenseFiles.length > 0) {
      try {
        licenseContent = await this.getFileContent(url, licenseFiles[0].path);
      } catch (error) {
        console.warn('Could not fetch LICENSE content');
      }
    }

    // Enhanced code documentation analysis
    const codeDocumentation = await this.analyzeCodeDocumentation(url, contents);
    const architectureDocumentation = await this.generateArchitectureDocumentation(url, contents);

    const hasDocumentation = readmeFiles.length > 0 || contributingFiles.length > 0;
    const documentationScore = this.calculateDocumentationScore(readmeContent, contributingContent, licenseContent, codeDocumentation);

    return {
      readme: readmeContent,
      contributing: contributingContent,
      license: licenseContent,
      hasDocumentation,
      documentationScore,
      codeDocumentation,
      architectureDocumentation,
    };
  }

  private async analyzeCodeDocumentation(url: string, contents: FileAnalysis[]) {
    const codeFiles = contents.filter(file => 
      file.type === 'file' && 
      ['JavaScript', 'TypeScript', 'Python', 'Java', 'Go', 'Rust', 'PHP', 'Ruby'].includes(this.getLanguageFromPath(file.path))
    );

    const documentationAnalysis = {
      totalCodeFiles: codeFiles.length,
      documentedFiles: 0,
      documentationCoverage: 0,
      codeComments: 0,
      functionDocumentation: 0,
      classDocumentation: 0,
      apiDocumentation: 0,
      inlineComments: 0,
      blockComments: 0,
      missingDocumentation: [] as string[],
      documentationQuality: {
        excellent: 0,
        good: 0,
        poor: 0,
        missing: 0,
      },
      suggestions: [] as string[],
    };

    for (const file of codeFiles.slice(0, 20)) { // Limit to first 20 files for performance
      try {
        const content = await this.getFileContent(url, file.path);
        const fileAnalysis = this.analyzeFileDocumentation(content, file.path, file.language);
        
        documentationAnalysis.documentedFiles += fileAnalysis.hasDocumentation ? 1 : 0;
        documentationAnalysis.codeComments += fileAnalysis.comments;
        documentationAnalysis.functionDocumentation += fileAnalysis.functionDocs;
        documentationAnalysis.classDocumentation += fileAnalysis.classDocs;
        documentationAnalysis.inlineComments += fileAnalysis.inlineComments;
        documentationAnalysis.blockComments += fileAnalysis.blockComments;
        
        if (!fileAnalysis.hasDocumentation) {
          documentationAnalysis.missingDocumentation.push(file.path);
        }
        
        documentationAnalysis.documentationQuality[fileAnalysis.quality as keyof typeof documentationAnalysis.documentationQuality]++;
        
        if (fileAnalysis.suggestions.length > 0) {
          documentationAnalysis.suggestions.push(...fileAnalysis.suggestions);
        }
      } catch (error) {
        console.warn(`Could not analyze file: ${file.path}`);
      }
    }

    // Calculate coverage
    documentationAnalysis.documentationCoverage = codeFiles.length > 0 
      ? Math.round((documentationAnalysis.documentedFiles / codeFiles.length) * 100)
      : 0;

    // Check for API documentation
    const apiDocFiles = contents.filter(file => 
      file.path.includes('api') || 
      file.path.includes('swagger') || 
      file.path.includes('openapi') ||
      file.path.includes('docs/api')
    );
    documentationAnalysis.apiDocumentation = apiDocFiles.length;

    return documentationAnalysis;
  }

  private analyzeFileDocumentation(content: string, filePath: string, language?: string): any {
    const analysis = {
      hasDocumentation: false,
      comments: 0,
      functionDocs: 0,
      classDocs: 0,
      inlineComments: 0,
      blockComments: 0,
      quality: 'missing' as 'excellent' | 'good' | 'poor' | 'missing',
      suggestions: [] as string[],
    };

    if (!content) return analysis;

    const lines = content.split('\n');
    let inBlockComment = false;
    let hasFunctionDoc = false;
    let hasClassDoc = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Count different types of comments based on language
      if (language === 'JavaScript' || language === 'TypeScript') {
        // Single line comments
        if (line.startsWith('//')) {
          analysis.inlineComments++;
          analysis.comments++;
        }
        // Block comments
        if (line.includes('/*')) {
          inBlockComment = true;
          analysis.blockComments++;
          analysis.comments++;
        }
        if (line.includes('*/')) {
          inBlockComment = false;
        }
        if (inBlockComment) {
          analysis.comments++;
        }
        // JSDoc comments
        if (line.startsWith('/**') || line.startsWith(' *')) {
          hasFunctionDoc = true;
          analysis.functionDocs++;
        }
      } else if (language === 'Python') {
        // Python comments
        if (line.startsWith('#')) {
          analysis.inlineComments++;
          analysis.comments++;
        }
        // Docstrings
        if (line.includes('"""') || line.includes("'''")) {
          hasFunctionDoc = true;
          analysis.functionDocs++;
        }
      } else if (language === 'Java') {
        // Java comments
        if (line.startsWith('//')) {
          analysis.inlineComments++;
          analysis.comments++;
        }
        // JavaDoc
        if (line.startsWith('/**') || line.startsWith(' *')) {
          hasFunctionDoc = true;
          analysis.functionDocs++;
        }
      }

      // Check for class definitions
      if (line.includes('class ') || line.includes('function ') || line.includes('def ')) {
        hasClassDoc = true;
        analysis.classDocs++;
      }
    }

    analysis.hasDocumentation = analysis.comments > 0 || hasFunctionDoc || hasClassDoc;

    // Determine quality
    if (analysis.comments > 10 && hasFunctionDoc) {
      analysis.quality = 'excellent';
    } else if (analysis.comments > 5 || hasFunctionDoc) {
      analysis.quality = 'good';
    } else if (analysis.comments > 0) {
      analysis.quality = 'poor';
    } else {
      analysis.quality = 'missing';
      analysis.suggestions.push(`Add documentation to ${filePath}`);
    }

    return analysis;
  }

  private async generateArchitectureDocumentation(url: string, contents: FileAnalysis[]) {
    const architecture = {
      overview: '',
      components: [] as any[],
      dataFlow: '',
      patterns: [] as string[],
      structure: {} as any,
      recommendations: [] as string[],
      layers: [] as any[],
      relationships: [] as any[],
      modules: [] as any[],
    };

    // Analyze project structure
    const directories = contents.filter(item => item.type === 'dir');
    const files = contents.filter(item => item.type === 'file');

    // Generate structure overview
    architecture.structure = this.generateProjectStructure(directories, files);

    // Detect architectural patterns
    architecture.patterns = this.detectArchitecturalPatterns(contents);

    // Generate component analysis
    architecture.components = this.analyzeComponents(contents);

    // Analyze layers and modules
    architecture.layers = await this.analyzeArchitectureLayers(url, contents);
    architecture.modules = await this.analyzeModules(url, contents);
    architecture.relationships = await this.analyzeRelationships(url, contents);

    // Generate architecture overview
    architecture.overview = this.generateArchitectureOverview(contents, architecture.patterns);

    // Generate data flow description
    architecture.dataFlow = this.generateDataFlowDescription(contents);

    // Generate recommendations
    architecture.recommendations = this.generateArchitectureRecommendations(contents, architecture.patterns);

    return architecture;
  }

  private generateProjectStructure(directories: FileAnalysis[], files: FileAnalysis[]) {
    const structure: any = {
      root: {},
      directories: {},
      fileTypes: {},
      organization: 'unknown',
    };

    // Analyze directory organization
    const hasSrc = directories.some(dir => dir.path === 'src');
    const hasComponents = directories.some(dir => dir.path.includes('components'));
    const hasServices = directories.some(dir => dir.path.includes('services'));
    const hasModels = directories.some(dir => dir.path.includes('models'));
    const hasUtils = directories.some(dir => dir.path.includes('utils') || dir.path.includes('helpers'));

    if (hasSrc && hasComponents) {
      structure.organization = 'component-based';
    } else if (hasServices && hasModels) {
      structure.organization = 'service-oriented';
    } else if (hasUtils && hasComponents) {
      structure.organization = 'utility-based';
    } else {
      structure.organization = 'flat';
    }

    // Count file types
    const fileTypes: { [key: string]: number } = {};
    files.forEach(file => {
      const ext = file.path.split('.').pop() || 'unknown';
      fileTypes[ext] = (fileTypes[ext] || 0) + 1;
    });
    structure.fileTypes = fileTypes;

    return structure;
  }

  private generateFolderStructure(contents: FileAnalysis[]): FolderNode {
    console.log('Generating folder structure from', contents.length, 'items');
    
    // Create a map to store directories
    const directoryMap = new Map<string, FolderNode>();
    
    // Create root node
    const root: FolderNode = {
      name: 'Repository Root',
      type: 'folder',
      path: '/',
      children: []
    };
    directoryMap.set('/', root);

    // Sort contents by path to ensure parent directories are processed first
    const sortedContents = [...contents].sort((a, b) => a.path.localeCompare(b.path));
    
    console.log('Sorted contents sample:', sortedContents.slice(0, 5));

    for (const item of sortedContents) {
      const pathParts = item.path.split('/').filter(part => part.length > 0);
      const fileName = pathParts[pathParts.length - 1];
      
      if (item.type === 'dir') {
        // Create directory node
        const dirNode: FolderNode = {
          name: fileName,
          type: 'folder',
          path: item.path,
          children: []
        };
        
        directoryMap.set(item.path, dirNode);
        
        // Add to parent directory
        const parentPath = pathParts.slice(0, -1).join('/') || '/';
        const parent = directoryMap.get(parentPath);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(dirNode);
        } else {
          // If parent doesn't exist, add to root
          root.children = root.children || [];
          root.children.push(dirNode);
        }
      } else {
        // Create file node
        const fileExt = fileName.split('.').pop() || '';
        const fileNode: FolderNode = {
          name: fileName,
          type: 'file',
          path: item.path,
          fileType: fileExt,
          size: item.size
        };
        
        // Add to parent directory
        const parentPath = pathParts.slice(0, -1).join('/') || '/';
        const parent = directoryMap.get(parentPath);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(fileNode);
        } else {
          // If parent doesn't exist, add to root
          root.children = root.children || [];
          root.children.push(fileNode);
        }
      }
    }

    console.log('Generated folder structure:', root);
    console.log('Root children count:', root.children?.length || 0);
    
    return root;
  }

  private detectArchitecturalPatterns(contents: FileAnalysis[]): string[] {
    const patterns: string[] = [];

    // Check for common patterns
    const hasComponents = contents.some(file => file.path.includes('components'));
    const hasPages = contents.some(file => file.path.includes('pages'));
    const hasServices = contents.some(file => file.path.includes('services'));
    const hasModels = contents.some(file => file.path.includes('models'));
    const hasControllers = contents.some(file => file.path.includes('controllers'));
    const hasRepositories = contents.some(file => file.path.includes('repositories'));
    const hasMiddlewares = contents.some(file => file.path.includes('middleware'));
    const hasAPI = contents.some(file => file.path.includes('api') || file.path.includes('routes'));
    const hasDatabase = contents.some(file => 
      file.path.includes('db') || 
      file.path.includes('database') || 
      file.path.includes('models') ||
      file.path.includes('entities')
    );
    const hasUtils = contents.some(file => 
      file.path.includes('utils') || 
      file.path.includes('helpers') ||
      file.path.includes('lib')
    );
    const hasConfig = contents.some(file => 
      file.path.includes('config') || 
      file.path.includes('settings') ||
      file.path.includes('env')
    );

    // React/Component-based patterns
    if (hasComponents && hasPages) {
      patterns.push('Component-Based Architecture');
    }
    
    // Service-oriented patterns
    if (hasServices && hasModels) {
      patterns.push('Service-Oriented Architecture');
    }
    
    // API patterns
    if (hasAPI) {
      patterns.push('API-First Architecture');
    }
    
    // Repository pattern
    if (hasControllers && hasRepositories) {
      patterns.push('Repository Pattern');
    }
    
    // Middleware pattern
    if (hasMiddlewares) {
      patterns.push('Middleware Pattern');
    }
    
    // Database patterns
    if (hasDatabase) {
      patterns.push('Data-Driven Architecture');
    }
    
    // Utility patterns
    if (hasUtils && hasComponents) {
      patterns.push('Utility-Based Architecture');
    }
    
    // Configuration patterns
    if (hasConfig) {
      patterns.push('Configuration-Driven');
    }

    // Hybrid patterns
    if (patterns.length > 2) {
      patterns.push('Hybrid Architecture');
    }

    if (patterns.length === 0) {
      patterns.push('Traditional/Monolithic Architecture');
    }

    return patterns;
  }

  private analyzeComponents(contents: FileAnalysis[]) {
    const components: any[] = [];

    // Analyze different types of components
    const componentFiles = contents.filter(file => 
      file.path.includes('components') || 
      file.path.includes('pages') ||
      file.path.includes('services') ||
      file.path.includes('models')
    );

    const componentTypes = {
      'UI Components': componentFiles.filter(f => f.path.includes('components')).length,
      'Pages/Views': componentFiles.filter(f => f.path.includes('pages')).length,
      'Services': componentFiles.filter(f => f.path.includes('services')).length,
      'Models': componentFiles.filter(f => f.path.includes('models')).length,
    };

    Object.entries(componentTypes).forEach(([type, count]) => {
      if (count > 0) {
        components.push({
          type,
          count,
          description: this.getComponentDescription(type, count),
        });
      }
    });

    return components;
  }

  private getComponentDescription(type: string, count: number): string {
    const descriptions: { [key: string]: string } = {
      'UI Components': count > 10 ? 'Well-organized component library' : 'Basic component structure',
      'Pages/Views': count > 5 ? 'Comprehensive page structure' : 'Simple page organization',
      'Services': count > 3 ? 'Good service layer separation' : 'Basic service structure',
      'Models': count > 2 ? 'Clear data model organization' : 'Simple model structure',
    };
    return descriptions[type] || 'Standard component organization';
  }

  private generateArchitectureOverview(contents: FileAnalysis[], patterns: string[]): string {
    const mainLanguage = this.getMainLanguage(contents);
    const hasTests = contents.some(file => file.path.includes('test') || file.path.includes('spec'));
    const hasCI = contents.some(file => file.path.includes('.github/workflows') || file.path.includes('.gitlab-ci'));

    let overview = `This project follows a ${patterns.join(', ')} approach. `;
    overview += `The codebase is primarily written in ${mainLanguage}. `;
    
    if (hasTests) {
      overview += 'The project includes comprehensive testing infrastructure. ';
    }
    
    if (hasCI) {
      overview += 'Continuous integration is implemented for automated testing and deployment. ';
    }

    overview += 'The architecture emphasizes separation of concerns and maintainable code structure.';

    return overview;
  }

  private generateDataFlowDescription(contents: FileAnalysis[]): string {
    const hasAPI = contents.some(file => file.path.includes('api'));
    const hasDatabase = contents.some(file => file.path.includes('db') || file.path.includes('database'));
    const hasServices = contents.some(file => file.path.includes('services'));

    let dataFlow = 'Data flows through the application in a structured manner. ';

    if (hasAPI && hasServices) {
      dataFlow += 'External requests are handled through API endpoints, processed by service layers, ';
    }

    if (hasDatabase) {
      dataFlow += 'and persisted in the database layer. ';
    } else {
      dataFlow += 'and managed through in-memory or external data sources. ';
    }

    dataFlow += 'The application follows a unidirectional data flow pattern for predictable state management.';

    return dataFlow;
  }

  private generateArchitectureRecommendations(contents: FileAnalysis[], patterns: string[]): string[] {
    const recommendations: string[] = [];

    const hasTests = contents.some(file => file.path.includes('test') || file.path.includes('spec'));
    const hasCI = contents.some(file => file.path.includes('.github/workflows') || file.path.includes('.gitlab-ci'));
    const hasDocs = contents.some(file => file.path.includes('docs') || file.path.includes('documentation'));

    if (!hasTests) {
      recommendations.push('Implement comprehensive unit and integration tests');
    }

    if (!hasCI) {
      recommendations.push('Set up continuous integration for automated testing and deployment');
    }

    if (!hasDocs) {
      recommendations.push('Add detailed API documentation and architecture diagrams');
    }

    if (patterns.includes('Traditional/Monolithic Architecture')) {
      recommendations.push('Consider implementing microservices for better scalability');
    }

    recommendations.push('Add code documentation and inline comments for better maintainability');
    recommendations.push('Implement error handling and logging throughout the application');

    return recommendations;
  }

  private getMainLanguage(contents: FileAnalysis[]): string {
    const languageCount: { [key: string]: number } = {};
    
    contents.forEach(file => {
      if (file.language && file.language !== 'Unknown') {
        languageCount[file.language] = (languageCount[file.language] || 0) + 1;
      }
    });

    const sortedLanguages = Object.entries(languageCount)
      .sort(([,a], [,b]) => b - a);

    return sortedLanguages.length > 0 ? sortedLanguages[0][0] : 'Unknown';
  }

  private calculateDocumentationScore(readme: string, contributing: string, license: string, codeDocumentation: any): number {
    let score = 0;
    
    // Basic documentation (40 points)
    if (readme) score += 20;
    if (contributing) score += 10;
    if (license) score += 10;
    
    // Code documentation (60 points)
    if (codeDocumentation) {
      score += Math.min(codeDocumentation.documentationCoverage * 0.3, 30);
      score += Math.min(codeDocumentation.codeComments / 10, 20);
      score += Math.min(codeDocumentation.functionDocumentation * 2, 10);
    }
    
    return Math.min(score, 100);
  }

  private async analyzeArchitecture(url: string, contents: FileAnalysis[]) {
    const mainTechnologies = this.extractMainTechnologies(contents);
    const dependencies = await this.extractDependencies(url, contents);
    const structure = this.organizeFileStructure(contents);
    const folderStructure = this.generateFolderStructure(contents);

    return {
      structure,
      mainTechnologies,
      architecturePattern: this.detectArchitecturePattern(structure),
      dependencies,
      folderStructure,
    };
  }

  private async analyzeBestPractices(url: string, contents: FileAnalysis[]) {
    const suggestions: string[] = [];
    const issues: string[] = [];
    const bestPractices: any = {};
    
    // 1. Testing Analysis
    const testingAnalysis = this.analyzeTestingPractices(contents);
    bestPractices.testing = testingAnalysis;
    
    // 2. CI/CD Analysis
    const cicdAnalysis = this.analyzeCICDPractices(contents);
    bestPractices.cicd = cicdAnalysis;
    
    // 3. Code Quality Analysis
    const codeQualityAnalysis = await this.analyzeCodeQualityPractices(url, contents);
    bestPractices.codeQuality = codeQualityAnalysis;
    
    // 4. Performance Analysis
    const performanceAnalysis = this.analyzePerformancePractices(contents);
    bestPractices.performance = performanceAnalysis;
    
    // 5. Security Practices Analysis
    const securityPracticesAnalysis = this.analyzeSecurityPractices(contents);
    bestPractices.securityPractices = securityPracticesAnalysis;
    
    // 6. Documentation Analysis
    const documentationAnalysis = this.analyzeDocumentationPractices(contents);
    bestPractices.documentation = documentationAnalysis;
    
    // 7. Dependency Management Analysis
    const dependencyAnalysis = this.analyzeDependencyPractices(contents);
    bestPractices.dependencies = dependencyAnalysis;
    
    // 8. Project Structure Analysis
    const structureAnalysis = this.analyzeProjectStructurePractices(contents);
    bestPractices.structure = structureAnalysis;
    
    // 9. Error Handling Analysis
    const errorHandlingAnalysis = this.analyzeErrorHandlingPractices(contents);
    bestPractices.errorHandling = errorHandlingAnalysis;
    
    // 10. Monitoring and Logging Analysis
    const monitoringAnalysis = this.analyzeMonitoringPractices(contents);
    bestPractices.monitoring = monitoringAnalysis;

    // Generate suggestions and issues based on analysis
    this.generateBestPracticesSuggestions(bestPractices, suggestions, issues);

    // Calculate overall score
    const overallScore = this.calculateComprehensiveBestPracticesScore(bestPractices);

    return {
      score: overallScore,
      suggestions,
      issues,
      bestPractices,
      codeQuality: {
        complexity: codeQualityAnalysis.complexity,
        maintainability: codeQualityAnalysis.maintainability,
        testCoverage: testingAnalysis.coverage,
        codeDuplication: codeQualityAnalysis.duplication,
        cyclomaticComplexity: codeQualityAnalysis.cyclomaticComplexity,
      },
    };
  }

  private analyzeTestingPractices(contents: FileAnalysis[]) {
    const testFiles = contents.filter(file => 
      file.path.includes('test') || 
      file.path.includes('spec') || 
      file.path.includes('__tests__') ||
      file.path.includes('tests/') ||
      file.path.includes('.test.') ||
      file.path.includes('.spec.')
    );

    const unitTests = testFiles.filter(file => 
      file.path.includes('unit') || 
      file.path.includes('unit-test') ||
      this.getFileName(file.path).includes('unit')
    );

    const integrationTests = testFiles.filter(file => 
      file.path.includes('integration') || 
      file.path.includes('integration-test') ||
      this.getFileName(file.path).includes('integration')
    );

    const e2eTests = testFiles.filter(file => 
      file.path.includes('e2e') || 
      file.path.includes('end-to-end') ||
      file.path.includes('cypress') ||
      file.path.includes('playwright') ||
      file.path.includes('selenium')
    );

    const testFrameworks = this.detectTestFrameworks(contents);
    const coverage = this.calculateTestCoverage(contents, testFiles);

    return {
      hasTests: testFiles.length > 0,
      totalTestFiles: testFiles.length,
      unitTests: unitTests.length,
      integrationTests: integrationTests.length,
      e2eTests: e2eTests.length,
      testFrameworks,
      coverage,
      testQuality: this.assessTestQuality(testFiles),
      suggestions: this.generateTestingSuggestions(testFiles, testFrameworks, coverage),
    };
  }

  private analyzeCICDPractices(contents: FileAnalysis[]) {
    const ciFiles = contents.filter(file => 
      file.path.includes('.github/workflows') || 
      file.path.includes('.gitlab-ci') || 
      file.path.includes('travis') ||
      file.path.includes('circleci') ||
      file.path.includes('jenkins') ||
      file.path.includes('azure-pipelines') ||
      file.path.includes('bitbucket-pipelines')
    );

    const hasCI = ciFiles.length > 0;
    const hasCD = ciFiles.some(file => 
      file.path.includes('deploy') || 
      file.path.includes('production') ||
      file.path.includes('release')
    );

    const ciPlatforms = this.detectCIPlatforms(ciFiles);
    const stages = this.analyzeCIStages(ciFiles);

    return {
      hasCI,
      hasCD,
      ciFiles: ciFiles.length,
      ciPlatforms,
      stages,
      quality: this.assessCIQuality(ciFiles),
      suggestions: this.generateCICDSuggestions(hasCI, hasCD, ciPlatforms, stages),
    };
  }

  private async analyzeCodeQualityPractices(url: string, contents: FileAnalysis[]) {
    const lintingFiles = contents.filter(file => 
      file.path.includes('eslint') || 
      file.path.includes('prettier') || 
      file.path.includes('pylint') ||
      file.path.includes('flake8') ||
      file.path.includes('rubocop') ||
      file.path.includes('gofmt') ||
      file.path.includes('rustfmt') ||
      file.path.includes('.editorconfig')
    );

    const formattingFiles = contents.filter(file => 
      file.path.includes('prettier') || 
      file.path.includes('black') ||
      file.path.includes('gofmt') ||
      file.path.includes('rustfmt')
    );

    // Use the new async methods for more accurate analysis
    const complexity = await this.calculateComplexity(url, contents);
    const maintainability = await this.calculateMaintainability(url, contents);
    const duplication = await this.calculateCodeDuplication(url, contents);
    const cyclomaticComplexity = this.calculateCyclomaticComplexity(contents);

    return {
      hasLinting: lintingFiles.length > 0,
      hasFormatting: formattingFiles.length > 0,
      lintingTools: this.detectLintingTools(lintingFiles),
      formattingTools: this.detectFormattingTools(formattingFiles),
      complexity,
      maintainability,
      duplication,
      cyclomaticComplexity,
      quality: this.assessCodeQuality(complexity, maintainability, duplication),
      suggestions: this.generateCodeQualitySuggestions(lintingFiles, formattingFiles, complexity, maintainability),
    };
  }

  private analyzePerformancePractices(contents: FileAnalysis[]) {
    const performanceFiles = contents.filter(file => 
      file.path.includes('performance') || 
      file.path.includes('benchmark') ||
      file.path.includes('load-test') ||
      file.path.includes('stress-test')
    );

    const cachingFiles = contents.filter(file => 
      file.path.includes('cache') || 
      file.path.includes('redis') ||
      file.path.includes('memcached')
    );

    const optimizationFiles = contents.filter(file => 
      file.path.includes('optimize') || 
      file.path.includes('performance') ||
      file.path.includes('bundle')
    );

    return {
      hasPerformanceTests: performanceFiles.length > 0,
      hasCaching: cachingFiles.length > 0,
      hasOptimization: optimizationFiles.length > 0,
      performanceFiles: performanceFiles.length,
      cachingStrategies: this.detectCachingStrategies(cachingFiles),
      optimizationTechniques: this.detectOptimizationTechniques(optimizationFiles),
      suggestions: this.generatePerformanceSuggestions(performanceFiles, cachingFiles, optimizationFiles),
    };
  }

  private analyzeSecurityPractices(contents: FileAnalysis[]) {
    const securityFiles = contents.filter(file => 
      file.path.includes('security') || 
      file.path.includes('auth') ||
      file.path.includes('authentication') ||
      file.path.includes('authorization')
    );

    const hasSecurityConfig = securityFiles.length > 0;
    const hasAuth = contents.some(file => 
      file.path.includes('auth') || 
      file.path.includes('login') ||
      file.path.includes('jwt') ||
      file.path.includes('oauth')
    );

    const securityTools = this.detectSecurityTools(contents);

    return {
      hasSecurityConfig,
      hasAuth,
      securityFiles: securityFiles.length,
      securityTools,
      suggestions: this.generateSecurityPracticeSuggestions(hasSecurityConfig, hasAuth, securityTools),
    };
  }

  private analyzeDocumentationPractices(contents: FileAnalysis[]) {
    const docFiles = contents.filter(file => 
      file.path.includes('docs') || 
      file.path.includes('documentation') ||
      file.path.includes('README') ||
      file.path.includes('CONTRIBUTING') ||
      file.path.includes('CHANGELOG')
    );

    const apiDocs = contents.filter(file => 
      file.path.includes('api') || 
      file.path.includes('swagger') ||
      file.path.includes('openapi')
    );

    const inlineDocs = this.analyzeInlineDocumentation(contents);

    return {
      hasDocumentation: docFiles.length > 0,
      hasAPIDocs: apiDocs.length > 0,
      docFiles: docFiles.length,
      apiDocs: apiDocs.length,
      inlineDocs,
      suggestions: this.generateDocumentationSuggestions(docFiles, apiDocs, inlineDocs),
    };
  }

  private analyzeDependencyPractices(contents: FileAnalysis[]) {
    const packageFiles = contents.filter(file => 
      file.path === 'package.json' || 
      file.path === 'requirements.txt' ||
      file.path === 'pom.xml' ||
      file.path === 'build.gradle' ||
      file.path === 'Cargo.toml' ||
      file.path === 'go.mod'
    );

    const lockFiles = contents.filter(file => 
      file.path === 'package-lock.json' || 
      file.path === 'yarn.lock' ||
      file.path === 'poetry.lock' ||
      file.path === 'Cargo.lock'
    );

    const hasDependencyManagement = packageFiles.length > 0;
    const hasLockFiles = lockFiles.length > 0;

    return {
      hasDependencyManagement,
      hasLockFiles,
      packageFiles: packageFiles.length,
      lockFiles: lockFiles.length,
      suggestions: this.generateDependencySuggestions(hasDependencyManagement, hasLockFiles),
    };
  }

  private analyzeProjectStructurePractices(contents: FileAnalysis[]) {
    const hasSrcFolder = contents.some(file => file.path.startsWith('src/'));
    const hasDistFolder = contents.some(file => file.path.startsWith('dist/') || file.path.startsWith('build/'));
    const hasConfigFolder = contents.some(file => file.path.startsWith('config/') || file.path.startsWith('conf/'));
    const hasAssetsFolder = contents.some(file => file.path.startsWith('assets/') || file.path.startsWith('public/'));

    const structureScore = this.calculateStructureScore(contents);

    return {
      hasSrcFolder,
      hasDistFolder,
      hasConfigFolder,
      hasAssetsFolder,
      structureScore,
      suggestions: this.generateStructureSuggestions(hasSrcFolder, hasDistFolder, hasConfigFolder, hasAssetsFolder),
    };
  }

  private analyzeErrorHandlingPractices(contents: FileAnalysis[]) {
    const errorHandlingFiles = contents.filter(file => 
      file.path.includes('error') || 
      file.path.includes('exception') ||
      file.path.includes('logging')
    );

    const hasErrorHandling = errorHandlingFiles.length > 0;
    const hasLogging = contents.some(file => 
      file.path.includes('log') || 
      file.path.includes('logger') ||
      file.path.includes('winston') ||
      file.path.includes('log4j')
    );

    return {
      hasErrorHandling,
      hasLogging,
      errorHandlingFiles: errorHandlingFiles.length,
      suggestions: this.generateErrorHandlingSuggestions(hasErrorHandling, hasLogging),
    };
  }

  private analyzeMonitoringPractices(contents: FileAnalysis[]) {
    const monitoringFiles = contents.filter(file => 
      file.path.includes('monitor') || 
      file.path.includes('metrics') ||
      file.path.includes('health') ||
      file.path.includes('prometheus') ||
      file.path.includes('grafana')
    );

    const hasMonitoring = monitoringFiles.length > 0;
    const hasHealthChecks = contents.some(file => 
      file.path.includes('health') || 
      file.path.includes('ping') ||
      file.path.includes('status')
    );

    return {
      hasMonitoring,
      hasHealthChecks,
      monitoringFiles: monitoringFiles.length,
      suggestions: this.generateMonitoringSuggestions(hasMonitoring, hasHealthChecks),
    };
  }

  // Helper methods for detailed analysis
  private detectTestFrameworks(contents: FileAnalysis[]): string[] {
    const frameworks: string[] = [];
    
    if (contents.some(file => file.path.includes('jest'))) frameworks.push('Jest');
    if (contents.some(file => file.path.includes('mocha'))) frameworks.push('Mocha');
    if (contents.some(file => file.path.includes('cypress'))) frameworks.push('Cypress');
    if (contents.some(file => file.path.includes('playwright'))) frameworks.push('Playwright');
    if (contents.some(file => file.path.includes('pytest'))) frameworks.push('pytest');
    if (contents.some(file => file.path.includes('junit'))) frameworks.push('JUnit');
    if (contents.some(file => file.path.includes('rspec'))) frameworks.push('RSpec');
    
    return frameworks;
  }

  private calculateTestCoverage(contents: FileAnalysis[], testFiles: FileAnalysis[]): number {
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java', 'Go', 'Rust'].includes(file.language)
    );
    
    if (codeFiles.length === 0) return 0;
    
    // Simplified coverage calculation based on test file ratio
    const coverage = Math.min((testFiles.length / codeFiles.length) * 100, 100);
    return Math.round(coverage);
  }

  private assessTestQuality(testFiles: FileAnalysis[]): string {
    if (testFiles.length === 0) return 'none';
    if (testFiles.length < 5) return 'poor';
    if (testFiles.length < 15) return 'fair';
    if (testFiles.length < 30) return 'good';
    return 'excellent';
  }

  private detectCIPlatforms(ciFiles: FileAnalysis[]): string[] {
    const platforms: string[] = [];
    
    if (ciFiles.some(file => file.path.includes('github'))) platforms.push('GitHub Actions');
    if (ciFiles.some(file => file.path.includes('gitlab'))) platforms.push('GitLab CI');
    if (ciFiles.some(file => file.path.includes('travis'))) platforms.push('Travis CI');
    if (ciFiles.some(file => file.path.includes('circleci'))) platforms.push('CircleCI');
    if (ciFiles.some(file => file.path.includes('jenkins'))) platforms.push('Jenkins');
    if (ciFiles.some(file => file.path.includes('azure'))) platforms.push('Azure DevOps');
    if (ciFiles.some(file => file.path.includes('bitbucket'))) platforms.push('Bitbucket Pipelines');
    
    return platforms;
  }

  private analyzeCIStages(ciFiles: FileAnalysis[]): string[] {
    const stages: string[] = [];
    
    if (ciFiles.some(file => file.path.includes('test'))) stages.push('Testing');
    if (ciFiles.some(file => file.path.includes('build'))) stages.push('Building');
    if (ciFiles.some(file => file.path.includes('deploy'))) stages.push('Deployment');
    if (ciFiles.some(file => file.path.includes('lint'))) stages.push('Linting');
    if (ciFiles.some(file => file.path.includes('security'))) stages.push('Security Scan');
    
    return stages;
  }

  private assessCIQuality(ciFiles: FileAnalysis[]): string {
    if (ciFiles.length === 0) return 'none';
    if (ciFiles.length < 2) return 'basic';
    if (ciFiles.length < 5) return 'good';
    return 'comprehensive';
  }

  private detectLintingTools(lintingFiles: FileAnalysis[]): string[] {
    const tools: string[] = [];
    
    if (lintingFiles.some(file => file.path.includes('eslint'))) tools.push('ESLint');
    if (lintingFiles.some(file => file.path.includes('prettier'))) tools.push('Prettier');
    if (lintingFiles.some(file => file.path.includes('pylint'))) tools.push('Pylint');
    if (lintingFiles.some(file => file.path.includes('flake8'))) tools.push('Flake8');
    if (lintingFiles.some(file => file.path.includes('rubocop'))) tools.push('RuboCop');
    if (lintingFiles.some(file => file.path.includes('gofmt'))) tools.push('gofmt');
    if (lintingFiles.some(file => file.path.includes('rustfmt'))) tools.push('rustfmt');
    
    return tools;
  }

  private detectFormattingTools(formattingFiles: FileAnalysis[]): string[] {
    const tools: string[] = [];
    
    if (formattingFiles.some(file => file.path.includes('prettier'))) tools.push('Prettier');
    if (formattingFiles.some(file => file.path.includes('black'))) tools.push('Black');
    if (formattingFiles.some(file => file.path.includes('gofmt'))) tools.push('gofmt');
    if (formattingFiles.some(file => file.path.includes('rustfmt'))) tools.push('rustfmt');
    
    return tools;
  }

  private async calculateCodeDuplication(url: string, contents: FileAnalysis[]): Promise<number> {
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    if (codeFiles.length < 2) return 0;
    
    // Estimate based on file count and complexity
    const estimatedDuplication = Math.min(codeFiles.length * 2, 30);
    return Math.round(estimatedDuplication);
  }

  private calculateCyclomaticComplexity(contents: FileAnalysis[]): number {
    // Simplified cyclomatic complexity calculation
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    if (codeFiles.length === 0) return 0;
    
    // Estimate based on file count
    const estimatedComplexity = Math.min(codeFiles.length * 3, 50);
    return Math.round(estimatedComplexity);
  }

  private assessCodeQuality(complexity: number, maintainability: number, duplication: number): string {
    const avgScore = (complexity + maintainability + (100 - duplication)) / 3;
    
    if (avgScore >= 80) return 'excellent';
    if (avgScore >= 60) return 'good';
    if (avgScore >= 40) return 'fair';
    return 'poor';
  }

  private detectCachingStrategies(cachingFiles: FileAnalysis[]): string[] {
    const strategies: string[] = [];
    
    if (cachingFiles.some(file => file.path.includes('redis'))) strategies.push('Redis');
    if (cachingFiles.some(file => file.path.includes('memcached'))) strategies.push('Memcached');
    if (cachingFiles.some(file => file.path.includes('cache'))) strategies.push('Application Cache');
    
    return strategies;
  }

  private detectOptimizationTechniques(optimizationFiles: FileAnalysis[]): string[] {
    const techniques: string[] = [];
    
    if (optimizationFiles.some(file => file.path.includes('bundle'))) techniques.push('Bundle Optimization');
    if (optimizationFiles.some(file => file.path.includes('performance'))) techniques.push('Performance Monitoring');
    if (optimizationFiles.some(file => file.path.includes('optimize'))) techniques.push('Code Optimization');
    
    return techniques;
  }

  private detectSecurityTools(contents: FileAnalysis[]): string[] {
    const tools: string[] = [];
    
    if (contents.some(file => file.path.includes('helmet'))) tools.push('Helmet.js');
    if (contents.some(file => file.path.includes('cors'))) tools.push('CORS');
    if (contents.some(file => file.path.includes('bcrypt'))) tools.push('bcrypt');
    if (contents.some(file => file.path.includes('jwt'))) tools.push('JWT');
    if (contents.some(file => file.path.includes('oauth'))) tools.push('OAuth');
    
    return tools;
  }

  private analyzeInlineDocumentation(contents: FileAnalysis[]): any {
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    let totalComments = 0;
    let documentedFunctions = 0;
    let documentedClasses = 0;
    
    // Simplified analysis
    codeFiles.forEach(file => {
      totalComments += Math.floor(Math.random() * 10); // Placeholder
      documentedFunctions += Math.floor(Math.random() * 5);
      documentedClasses += Math.floor(Math.random() * 3);
    });
    
    return {
      totalComments,
      documentedFunctions,
      documentedClasses,
      coverage: codeFiles.length > 0 ? Math.round((documentedFunctions + documentedClasses) / codeFiles.length * 100) : 0,
    };
  }

  private calculateStructureScore(contents: FileAnalysis[]): number {
    let score = 0;
    
    if (contents.some(file => file.path.startsWith('src/'))) score += 25;
    if (contents.some(file => file.path.startsWith('dist/') || file.path.startsWith('build/'))) score += 20;
    if (contents.some(file => file.path.startsWith('config/') || file.path.startsWith('conf/'))) score += 20;
    if (contents.some(file => file.path.startsWith('assets/') || file.path.startsWith('public/'))) score += 15;
    if (contents.some(file => file.path.startsWith('tests/') || file.path.startsWith('test/'))) score += 20;
    
    return score;
  }

  // Suggestion generation methods
  private generateTestingSuggestions(testFiles: FileAnalysis[], frameworks: string[], coverage: number): string[] {
    const suggestions: string[] = [];
    
    if (testFiles.length === 0) {
      suggestions.push('Implement comprehensive unit tests using Jest or similar framework');
      suggestions.push('Add integration tests for API endpoints and database operations');
      suggestions.push('Consider adding end-to-end tests with Cypress or Playwright');
    } else if (testFiles.length < 5) {
      suggestions.push('Expand test coverage to include more edge cases');
      suggestions.push('Add tests for error handling scenarios');
    }
    
    if (coverage < 50) {
      suggestions.push('Aim for at least 80% test coverage');
      suggestions.push('Add tests for critical business logic');
    }
    
    if (frameworks.length === 0) {
      suggestions.push('Choose and configure a testing framework');
    }
    
    return suggestions;
  }

  private generateCICDSuggestions(hasCI: boolean, hasCD: boolean, platforms: string[], stages: string[]): string[] {
    const suggestions: string[] = [];
    
    if (!hasCI) {
      suggestions.push('Set up continuous integration with GitHub Actions or similar');
      suggestions.push('Configure automated testing on every pull request');
    }
    
    if (!hasCD) {
      suggestions.push('Implement continuous deployment for automated releases');
      suggestions.push('Add deployment stages to CI/CD pipeline');
    }
    
    if (!stages.includes('Testing')) {
      suggestions.push('Add testing stage to CI/CD pipeline');
    }
    
    if (!stages.includes('Linting')) {
      suggestions.push('Add code linting and formatting checks to CI');
    }
    
    if (!stages.includes('Security Scan')) {
      suggestions.push('Integrate security scanning into CI/CD pipeline');
    }
    
    return suggestions;
  }

  private generateCodeQualitySuggestions(lintingFiles: FileAnalysis[], formattingFiles: FileAnalysis[], complexity: number, maintainability: number): string[] {
    const suggestions: string[] = [];
    
    if (lintingFiles.length === 0) {
      suggestions.push('Add ESLint or similar linting tool for code quality');
      suggestions.push('Configure linting rules for consistent code style');
    }
    
    if (formattingFiles.length === 0) {
      suggestions.push('Add Prettier or similar code formatter');
      suggestions.push('Configure automatic code formatting on save');
    }
    
    if (complexity > 70) {
      suggestions.push('Reduce code complexity by breaking down large functions');
      suggestions.push('Consider refactoring complex logic into smaller modules');
    }
    
    if (maintainability < 60) {
      suggestions.push('Improve code maintainability with better documentation');
      suggestions.push('Follow consistent naming conventions');
    }
    
    return suggestions;
  }

  private generatePerformanceSuggestions(performanceFiles: FileAnalysis[], cachingFiles: FileAnalysis[], optimizationFiles: FileAnalysis[]): string[] {
    const suggestions: string[] = [];
    
    if (performanceFiles.length === 0) {
      suggestions.push('Add performance testing and benchmarking');
      suggestions.push('Implement load testing for critical endpoints');
    }
    
    if (cachingFiles.length === 0) {
      suggestions.push('Consider implementing caching strategies');
      suggestions.push('Add Redis or similar caching solution');
    }
    
    if (optimizationFiles.length === 0) {
      suggestions.push('Implement code optimization techniques');
      suggestions.push('Add bundle optimization for frontend applications');
    }
    
    return suggestions;
  }

  private generateSecurityPracticeSuggestions(hasSecurityConfig: boolean, hasAuth: boolean, securityTools: string[]): string[] {
    const suggestions: string[] = [];
    
    if (!hasSecurityConfig) {
      suggestions.push('Implement security configuration and best practices');
      suggestions.push('Add security headers and CORS configuration');
    }
    
    if (!hasAuth) {
      suggestions.push('Implement proper authentication and authorization');
      suggestions.push('Add JWT or OAuth authentication');
    }
    
    if (!securityTools.includes('Helmet.js')) {
      suggestions.push('Add Helmet.js for security headers');
    }
    
    if (!securityTools.includes('bcrypt')) {
      suggestions.push('Use bcrypt for password hashing');
    }
    
    return suggestions;
  }

  private generateDocumentationSuggestions(docFiles: FileAnalysis[], apiDocs: FileAnalysis[], inlineDocs: any): string[] {
    const suggestions: string[] = [];
    
    if (docFiles.length === 0) {
      suggestions.push('Add comprehensive README with setup instructions');
      suggestions.push('Create contributing guidelines for developers');
      suggestions.push('Add API documentation');
    }
    
    if (apiDocs.length === 0) {
      suggestions.push('Generate API documentation using Swagger/OpenAPI');
      suggestions.push('Document all API endpoints and parameters');
    }
    
    if (inlineDocs.coverage < 50) {
      suggestions.push('Add inline documentation to functions and classes');
      suggestions.push('Use JSDoc or similar for function documentation');
    }
    
    return suggestions;
  }

  private generateDependencySuggestions(hasDependencyManagement: boolean, hasLockFiles: boolean): string[] {
    const suggestions: string[] = [];
    
    if (!hasDependencyManagement) {
      suggestions.push('Add proper dependency management (package.json, requirements.txt, etc.)');
    }
    
    if (!hasLockFiles) {
      suggestions.push('Use lock files to ensure consistent dependency versions');
      suggestions.push('Add package-lock.json or yarn.lock for Node.js projects');
    }
    
    return suggestions;
  }

  private generateStructureSuggestions(hasSrcFolder: boolean, hasDistFolder: boolean, hasConfigFolder: boolean, hasAssetsFolder: boolean): string[] {
    const suggestions: string[] = [];
    
    if (!hasSrcFolder) {
      suggestions.push('Organize source code in a src/ directory');
    }
    
    if (!hasDistFolder) {
      suggestions.push('Add build output directory (dist/ or build/)');
    }
    
    if (!hasConfigFolder) {
      suggestions.push('Create config/ directory for configuration files');
    }
    
    if (!hasAssetsFolder) {
      suggestions.push('Organize static assets in assets/ or public/ directory');
    }
    
    return suggestions;
  }

  private generateErrorHandlingSuggestions(hasErrorHandling: boolean, hasLogging: boolean): string[] {
    const suggestions: string[] = [];
    
    if (!hasErrorHandling) {
      suggestions.push('Implement comprehensive error handling');
      suggestions.push('Add try-catch blocks for critical operations');
    }
    
    if (!hasLogging) {
      suggestions.push('Add logging framework for debugging and monitoring');
      suggestions.push('Implement structured logging with Winston or similar');
    }
    
    return suggestions;
  }

  private generateMonitoringSuggestions(hasMonitoring: boolean, hasHealthChecks: boolean): string[] {
    const suggestions: string[] = [];
    
    if (!hasMonitoring) {
      suggestions.push('Implement application monitoring and metrics');
      suggestions.push('Add Prometheus or similar monitoring solution');
    }
    
    if (!hasHealthChecks) {
      suggestions.push('Add health check endpoints for monitoring');
      suggestions.push('Implement readiness and liveness probes');
    }
    
    return suggestions;
  }

  private generateBestPracticesSuggestions(bestPractices: any, suggestions: string[], issues: string[]) {
    // Add suggestions from all analysis areas
    Object.values(bestPractices).forEach((analysis: any) => {
      if (analysis.suggestions) {
        suggestions.push(...analysis.suggestions);
      }
    });

    // Add critical issues
    if (!bestPractices.testing.hasTests) {
      issues.push('No test files found - critical for code quality');
    }
    
    if (!bestPractices.cicd.hasCI) {
      issues.push('No CI/CD pipeline found - critical for deployment automation');
    }
    
    if (!bestPractices.codeQuality.hasLinting) {
      issues.push('No code linting configured - important for code quality');
    }
  }

  private calculateComprehensiveBestPracticesScore(bestPractices: any): number {
    let score = 0;
    const weights = {
      testing: 0.25,
      cicd: 0.20,
      codeQuality: 0.20,
      security: 0.15,
      documentation: 0.10,
      structure: 0.10,
    };

    // Testing score
    if (bestPractices.testing.hasTests) score += weights.testing * 100;
    if (bestPractices.testing.coverage > 80) score += weights.testing * 20;
    else if (bestPractices.testing.coverage > 50) score += weights.testing * 10;

    // CI/CD score
    if (bestPractices.cicd.hasCI) score += weights.cicd * 100;
    if (bestPractices.cicd.hasCD) score += weights.cicd * 20;

    // Code quality score
    if (bestPractices.codeQuality.hasLinting) score += weights.codeQuality * 50;
    if (bestPractices.codeQuality.hasFormatting) score += weights.codeQuality * 30;
    score += weights.codeQuality * (bestPractices.codeQuality.maintainability / 100) * 20;

    // Security score
    if (bestPractices.securityPractices.hasSecurityConfig) score += weights.security * 50;
    if (bestPractices.securityPractices.hasAuth) score += weights.security * 50;

    // Documentation score
    if (bestPractices.documentation.hasDocumentation) score += weights.documentation * 100;

    // Structure score
    score += weights.structure * (bestPractices.structure.structureScore / 100) * 100;

    return Math.round(score);
  }

  private async calculateComplexity(url: string, contents: FileAnalysis[]): Promise<number> {
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java', 'Go', 'Rust', 'PHP', 'Ruby'].includes(file.language) &&
      file.type === 'file'
    );

    if (codeFiles.length === 0) return 0;

    let totalComplexity = 0;
    let analyzedFiles = 0;
    const maxFilesToAnalyze = Math.min(codeFiles.length, 20); // Limit for performance

    // Analyze a sample of files for actual complexity
    for (const file of codeFiles.slice(0, maxFilesToAnalyze)) {
      try {
        const content = await this.getFileContent(url, file.path);
        const fileComplexity = this.analyzeFileComplexity(content, file.language);
        totalComplexity += fileComplexity;
        analyzedFiles++;
      } catch (error) {
        console.warn(`Could not analyze complexity for ${file.path}`);
      }
    }

    // Calculate average complexity and normalize
    const averageComplexity = analyzedFiles > 0 ? totalComplexity / analyzedFiles : 0;
    
    // Normalize to 0-100 scale based on industry standards
    // 0-10: Very Low, 11-25: Low, 26-50: Medium, 51-75: High, 76-100: Very High
    const normalizedComplexity = Math.min(Math.max(averageComplexity * 2, 0), 100);
    
    return Math.round(normalizedComplexity);
  }

  private analyzeFileComplexity(content: string, language?: string): number {
    if (!content || !language) return 0;

    let complexity = 0;
    const lines = content.split('\n');
    
    // Base complexity from file size
    complexity += Math.min(lines.length / 10, 10);

    // Language-specific complexity analysis
    switch (language) {
      case 'JavaScript':
      case 'TypeScript':
        complexity += this.analyzeJavaScriptComplexity(content);
        break;
      case 'Python':
        complexity += this.analyzePythonComplexity(content);
        break;
      case 'Java':
        complexity += this.analyzeJavaComplexity(content);
        break;
      case 'Go':
        complexity += this.analyzeGoComplexity(content);
        break;
      case 'Rust':
        complexity += this.analyzeRustComplexity(content);
        break;
      case 'PHP':
        complexity += this.analyzePHPComplexity(content);
        break;
      case 'Ruby':
        complexity += this.analyzeRubyComplexity(content);
        break;
    }

    return Math.round(complexity);
  }

  private analyzeJavaScriptComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s*\(/g) || []).length;
    const forLoops = (content.match(/for\s*\(/g) || []).length;
    const whileLoops = (content.match(/while\s*\(/g) || []).length;
    const switchStatements = (content.match(/switch\s*\(/g) || []).length;
    const catchBlocks = (content.match(/catch\s*\(/g) || []).length;
    const ternaryOperators = (content.match(/\?\s*[^:]+:/g) || []).length;
    const logicalOperators = (content.match(/&&|\|\|/g) || []).length;
    
    // Count function definitions
    const functionDefs = (content.match(/function\s+\w+|const\s+\w+\s*=\s*\(|let\s+\w+\s*=\s*\(|var\s+\w+\s*=\s*\(|=>\s*{/g) || []).length;
    
    // Count nested structures
    const nestedLevels = this.countNestedLevels(content);
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += switchStatements * 3;
    complexity += catchBlocks * 2;
    complexity += ternaryOperators * 1;
    complexity += logicalOperators * 0.5;
    complexity += functionDefs * 1;
    complexity += nestedLevels * 2;
    
    return complexity;
  }

  private analyzePythonComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s+/g) || []).length;
    const forLoops = (content.match(/for\s+\w+\s+in/g) || []).length;
    const whileLoops = (content.match(/while\s+/g) || []).length;
    const tryBlocks = (content.match(/try:/g) || []).length;
    const exceptBlocks = (content.match(/except/g) || []).length;
    const listComprehensions = (content.match(/\[.*for.*in.*\]/g) || []).length;
    
    // Count function definitions
    const functionDefs = (content.match(/def\s+\w+/g) || []).length;
    const classDefs = (content.match(/class\s+\w+/g) || []).length;
    
    // Count nested structures
    const nestedLevels = this.countPythonNestedLevels(content);
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += tryBlocks * 2;
    complexity += exceptBlocks * 1;
    complexity += listComprehensions * 1;
    complexity += functionDefs * 1;
    complexity += classDefs * 2;
    complexity += nestedLevels * 2;
    
    return complexity;
  }

  private analyzeJavaComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s*\(/g) || []).length;
    const forLoops = (content.match(/for\s*\(/g) || []).length;
    const whileLoops = (content.match(/while\s*\(/g) || []).length;
    const switchStatements = (content.match(/switch\s*\(/g) || []).length;
    const catchBlocks = (content.match(/catch\s*\(/g) || []).length;
    
    // Count method definitions
    const methodDefs = (content.match(/(public|private|protected)\s+\w+\s+\w+\s*\(/g) || []).length;
    const classDefs = (content.match(/class\s+\w+/g) || []).length;
    
    // Count nested structures
    const nestedLevels = this.countNestedLevels(content);
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += switchStatements * 3;
    complexity += catchBlocks * 2;
    complexity += methodDefs * 1;
    complexity += classDefs * 2;
    complexity += nestedLevels * 2;
    
    return complexity;
  }

  private analyzeGoComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s+/g) || []).length;
    const forLoops = (content.match(/for\s+/g) || []).length;
    const switchStatements = (content.match(/switch\s+/g) || []).length;
    const selectStatements = (content.match(/select\s*{/g) || []).length;
    
    // Count function definitions
    const functionDefs = (content.match(/func\s+\w+/g) || []).length;
    const structDefs = (content.match(/type\s+\w+\s+struct/g) || []).length;
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += switchStatements * 3;
    complexity += selectStatements * 3;
    complexity += functionDefs * 1;
    complexity += structDefs * 2;
    
    return complexity;
  }

  private analyzeRustComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s+/g) || []).length;
    const forLoops = (content.match(/for\s+/g) || []).length;
    const whileLoops = (content.match(/while\s+/g) || []).length;
    const matchStatements = (content.match(/match\s+/g) || []).length;
    
    // Count function definitions
    const functionDefs = (content.match(/fn\s+\w+/g) || []).length;
    const structDefs = (content.match(/struct\s+\w+/g) || []).length;
    const enumDefs = (content.match(/enum\s+\w+/g) || []).length;
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += matchStatements * 3;
    complexity += functionDefs * 1;
    complexity += structDefs * 2;
    complexity += enumDefs * 2;
    
    return complexity;
  }

  private analyzePHPComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s*\(/g) || []).length;
    const forLoops = (content.match(/for\s*\(/g) || []).length;
    const whileLoops = (content.match(/while\s*\(/g) || []).length;
    const foreachLoops = (content.match(/foreach\s*\(/g) || []).length;
    const switchStatements = (content.match(/switch\s*\(/g) || []).length;
    
    // Count function definitions
    const functionDefs = (content.match(/function\s+\w+/g) || []).length;
    const classDefs = (content.match(/class\s+\w+/g) || []).length;
    
    complexity += ifStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += foreachLoops * 2;
    complexity += switchStatements * 3;
    complexity += functionDefs * 1;
    complexity += classDefs * 2;
    
    return complexity;
  }

  private analyzeRubyComplexity(content: string): number {
    let complexity = 0;
    
    // Count control flow statements
    const ifStatements = (content.match(/if\s+/g) || []).length;
    const unlessStatements = (content.match(/unless\s+/g) || []).length;
    const forLoops = (content.match(/for\s+/g) || []).length;
    const whileLoops = (content.match(/while\s+/g) || []).length;
    const caseStatements = (content.match(/case\s+/g) || []).length;
    
    // Count method definitions
    const methodDefs = (content.match(/def\s+\w+/g) || []).length;
    const classDefs = (content.match(/class\s+\w+/g) || []).length;
    const moduleDefs = (content.match(/module\s+\w+/g) || []).length;
    
    complexity += ifStatements * 1;
    complexity += unlessStatements * 1;
    complexity += forLoops * 2;
    complexity += whileLoops * 2;
    complexity += caseStatements * 3;
    complexity += methodDefs * 1;
    complexity += classDefs * 2;
    complexity += moduleDefs * 2;
    
    return complexity;
  }

  private countNestedLevels(content: string): number {
    let maxDepth = 0;
    let currentDepth = 0;
    
    for (const char of content) {
      if (char === '{') {
        currentDepth++;
        maxDepth = Math.max(maxDepth, currentDepth);
      } else if (char === '}') {
        currentDepth = Math.max(0, currentDepth - 1);
      }
    }
    
    return maxDepth;
  }

  private countPythonNestedLevels(content: string): number {
    let maxDepth = 0;
    let currentDepth = 0;
    
    for (const line of content.split('\n')) {
      const leadingSpaces = line.match(/^(\s*)/)?.[1].length || 0;
      const indentLevel = Math.floor(leadingSpaces / 4); // Assuming 4 spaces per indent
      currentDepth = indentLevel;
      maxDepth = Math.max(maxDepth, currentDepth);
    }
    
    return maxDepth;
  }

  private async calculateMaintainability(url: string, contents: FileAnalysis[]): Promise<number> {
    let score = 0;
    const maxScore = 100;
    
    // 1. Documentation Quality (25 points)
    const documentationScore = await this.calculateDocumentationMaintainability(url, contents);
    score += documentationScore * 0.25;
    
    // 2. Code Structure (25 points)
    const structureScore = this.calculateStructureMaintainability(contents);
    score += structureScore * 0.25;
    
    // 3. Testing Coverage (20 points)
    const testingScore = this.calculateTestingMaintainability(contents);
    score += testingScore * 0.20;
    
    // 4. Code Quality Tools (15 points)
    const qualityToolsScore = this.calculateQualityToolsMaintainability(contents);
    score += qualityToolsScore * 0.15;
    
    // 5. Error Handling (15 points)
    const errorHandlingScore = await this.calculateErrorHandlingMaintainability(url, contents);
    score += errorHandlingScore * 0.15;
    
    return Math.round(Math.min(score, maxScore));
  }

  private async calculateDocumentationMaintainability(url: string, contents: FileAnalysis[]): Promise<number> {
    let score = 0;
    
    // Check for README files
    const readmeFiles = contents.filter(file => 
      file.path.toLowerCase().includes('readme') && file.type === 'file'
    );
    if (readmeFiles.length > 0) score += 30;
    
    // Check for API documentation
    const apiDocs = contents.filter(file => 
      file.path.includes('api') || file.path.includes('swagger') || file.path.includes('openapi')
    );
    if (apiDocs.length > 0) score += 20;
    
    // Check for inline documentation
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    let documentedFiles = 0;
    const sampleSize = Math.min(codeFiles.length, 10);
    
    for (const file of codeFiles.slice(0, sampleSize)) {
      try {
        const content = await this.getFileContent(url, file.path);
        if (this.hasInlineDocumentation(content, file.language)) {
          documentedFiles++;
        }
      } catch (error) {
        // Skip files that can't be read
      }
    }
    
    const documentationCoverage = sampleSize > 0 ? (documentedFiles / sampleSize) * 100 : 0;
    score += Math.min(documentationCoverage * 0.5, 50); // Max 50 points for inline docs
    
    return score;
  }

  private hasInlineDocumentation(content: string, language?: string): boolean {
    if (!content || !language) return false;
    
    const lines = content.split('\n');
    let commentLines = 0;
    
    switch (language) {
      case 'JavaScript':
      case 'TypeScript':
        commentLines = lines.filter(line => 
          line.trim().startsWith('//') || 
          line.trim().startsWith('/*') || 
          line.trim().startsWith('*') ||
          line.includes('/**') ||
          line.includes('@param') ||
          line.includes('@return')
        ).length;
        break;
      case 'Python':
        commentLines = lines.filter(line => 
          line.trim().startsWith('#') || 
          line.trim().startsWith('"""') ||
          line.trim().startsWith("'''")
        ).length;
        break;
      case 'Java':
        commentLines = lines.filter(line => 
          line.trim().startsWith('//') || 
          line.trim().startsWith('/*') || 
          line.trim().startsWith('*') ||
          line.includes('/**') ||
          line.includes('@param') ||
          line.includes('@return')
        ).length;
        break;
    }
    
    return commentLines > lines.length * 0.1; // At least 10% of lines should be comments
  }

  private calculateStructureMaintainability(contents: FileAnalysis[]): number {
    let score = 0;
    
    // Check for proper folder structure
    const hasSrcFolder = contents.some(file => file.path.startsWith('src/'));
    const hasDistFolder = contents.some(file => file.path.startsWith('dist/') || file.path.startsWith('build/'));
    const hasConfigFolder = contents.some(file => file.path.startsWith('config/') || file.path.startsWith('conf/'));
    const hasAssetsFolder = contents.some(file => file.path.startsWith('assets/') || file.path.startsWith('public/'));
    const hasTestsFolder = contents.some(file => file.path.startsWith('tests/') || file.path.startsWith('test/'));
    
    if (hasSrcFolder) score += 20;
    if (hasDistFolder) score += 15;
    if (hasConfigFolder) score += 15;
    if (hasAssetsFolder) score += 15;
    if (hasTestsFolder) score += 15;
    
    // Check for separation of concerns
    const hasComponents = contents.some(file => file.path.includes('components'));
    const hasServices = contents.some(file => file.path.includes('services'));
    const hasModels = contents.some(file => file.path.includes('models'));
    const hasUtils = contents.some(file => file.path.includes('utils') || file.path.includes('helpers'));
    
    if (hasComponents) score += 10;
    if (hasServices) score += 10;
    if (hasModels) score += 10;
    if (hasUtils) score += 10;
    
    return Math.min(score, 100);
  }

  private calculateTestingMaintainability(contents: FileAnalysis[]): number {
    let score = 0;
    
    // Count test files
    const testFiles = contents.filter(file => 
      file.path.includes('test') || 
      file.path.includes('spec') || 
      file.path.includes('__tests__') ||
      file.path.endsWith('.test.js') ||
      file.path.endsWith('.test.ts') ||
      file.path.endsWith('.test.py') ||
      file.path.endsWith('.spec.js') ||
      file.path.endsWith('.spec.ts')
    );
    
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    if (codeFiles.length === 0) return 100; // No code to test
    
    const testRatio = testFiles.length / codeFiles.length;
    
    if (testRatio >= 0.5) score = 100; // Excellent test coverage
    else if (testRatio >= 0.3) score = 80; // Good test coverage
    else if (testRatio >= 0.2) score = 60; // Moderate test coverage
    else if (testRatio >= 0.1) score = 40; // Low test coverage
    else if (testRatio > 0) score = 20; // Minimal test coverage
    else score = 0; // No tests
    
    return score;
  }

  private calculateQualityToolsMaintainability(contents: FileAnalysis[]): number {
    let score = 0;
    
    // Check for linting tools
    const hasLinting = contents.some(file => 
      file.path.includes('.eslintrc') || 
      file.path.includes('.pylintrc') || 
      file.path.includes('pylint') ||
      file.path.includes('flake8') ||
      file.path.includes('checkstyle')
    );
    
    // Check for formatting tools
    const hasFormatting = contents.some(file => 
      file.path.includes('.prettierrc') || 
      file.path.includes('black') ||
      file.path.includes('autopep8') ||
      file.path.includes('gofmt') ||
      file.path.includes('rustfmt')
    );
    
    // Check for type checking
    const hasTypeChecking = contents.some(file => 
      file.path.includes('tsconfig.json') || 
      file.path.includes('mypy') ||
      file.path.includes('pyright')
    );
    
    if (hasLinting) score += 40;
    if (hasFormatting) score += 30;
    if (hasTypeChecking) score += 30;
    
    return score;
  }

  private async calculateErrorHandlingMaintainability(url: string, contents: FileAnalysis[]): Promise<number> {
    let score = 0;
    
    const codeFiles = contents.filter(file => 
      file.language && ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(file.language)
    );
    
    let filesWithErrorHandling = 0;
    const sampleSize = Math.min(codeFiles.length, 10);
    
    for (const file of codeFiles.slice(0, sampleSize)) {
      try {
        const content = await this.getFileContent(url, file.path);
        if (this.hasErrorHandling(content, file.language)) {
          filesWithErrorHandling++;
        }
      } catch (error) {
        // Skip files that can't be read
      }
    }
    
    const errorHandlingCoverage = sampleSize > 0 ? (filesWithErrorHandling / sampleSize) * 100 : 0;
    score += errorHandlingCoverage;
    
    return score;
  }

  private hasErrorHandling(content: string, language?: string): boolean {
    if (!content || !language) return false;
    
    switch (language) {
      case 'JavaScript':
      case 'TypeScript':
        return (content.includes('try') && content.includes('catch')) ||
               content.includes('throw') ||
               content.includes('Error') ||
               content.includes('Promise.reject');
      case 'Python':
        return (content.includes('try:') && content.includes('except')) ||
               content.includes('raise') ||
               content.includes('Exception');
      case 'Java':
        return (content.includes('try') && content.includes('catch')) ||
               content.includes('throw') ||
               content.includes('Exception');
      default:
        return false;
    }
  }

  private extractMainTechnologies(contents: FileAnalysis[]): string[] {
    const languages = contents
      .filter(file => file.language && file.language !== 'Unknown')
      .map(file => file.language!)
      .filter((value, index, self) => self.indexOf(value) === index);
    
    return languages.slice(0, 5); // Top 5 technologies
  }

  private async extractDependencies(url: string, contents: FileAnalysis[]) {
    const dependencies: { [key: string]: string } = {};
    
    const packageJson = contents.find(file => file.path === 'package.json');
    if (packageJson) {
      try {
        const content = await this.getFileContent(url, 'package.json');
        const packageData = JSON.parse(content);
        
        // Extract all types of dependencies
        if (packageData.dependencies) {
          Object.assign(dependencies, packageData.dependencies);
        }
        if (packageData.devDependencies) {
          Object.assign(dependencies, packageData.devDependencies);
        }
        if (packageData.peerDependencies) {
          Object.assign(dependencies, packageData.peerDependencies);
        }
        if (packageData.optionalDependencies) {
          Object.assign(dependencies, packageData.optionalDependencies);
        }
      } catch (error) {
        console.warn('Could not parse package.json:', error);
      }
    }

    // If no package.json found, try to extract from other package files
    if (Object.keys(dependencies).length === 0) {
      const requirementsTxt = contents.find(file => file.path === 'requirements.txt');
      if (requirementsTxt) {
        try {
          const content = await this.getFileContent(url, 'requirements.txt');
          const lines = content.split('\n').filter(line => line.trim() && !line.startsWith('#'));
          lines.forEach(line => {
            const [packageName, version] = line.split('==');
            if (packageName && version) {
              dependencies[packageName.trim()] = version.trim();
            }
          });
        } catch (error) {
          console.warn('Could not parse requirements.txt:', error);
        }
      }
    }

    // If still no dependencies, try to extract from other common files
    if (Object.keys(dependencies).length === 0) {
      const pomXml = contents.find(file => file.path === 'pom.xml');
      if (pomXml) {
        try {
          const content = await this.getFileContent(url, 'pom.xml');
          // Simple regex to extract dependencies from pom.xml
          const dependencyMatches = content.match(/<artifactId>([^<]+)<\/artifactId>\s*<version>([^<]+)<\/version>/g);
          if (dependencyMatches) {
            dependencyMatches.forEach(match => {
              const artifactMatch = match.match(/<artifactId>([^<]+)<\/artifactId>/);
              const versionMatch = match.match(/<version>([^<]+)<\/version>/);
              if (artifactMatch && versionMatch) {
                dependencies[artifactMatch[1]] = versionMatch[1];
              }
            });
          }
        } catch (error) {
          console.warn('Could not parse pom.xml:', error);
        }
      }
    }

    return dependencies;
  }

  private organizeFileStructure(contents: FileAnalysis[]): FileAnalysis[] {
    return contents
      .filter(file => file.type === 'file')
      .sort((a, b) => a.path.localeCompare(b.path));
  }

  private detectArchitecturePattern(structure: FileAnalysis[]): string {
    const hasComponents = structure.some(file => file.path.includes('components'));
    const hasPages = structure.some(file => file.path.includes('pages'));
    const hasServices = structure.some(file => file.path.includes('services'));
    const hasModels = structure.some(file => file.path.includes('models'));

    if (hasComponents && hasPages) return 'React/Component-based';
    if (hasServices && hasModels) return 'Service-oriented';
    if (hasComponents && hasServices) return 'Hybrid';
    
    return 'Traditional';
  }

  private async analyzeArchitectureLayers(url: string, contents: FileAnalysis[]) {
    const layers = [];

    // Presentation Layer
    const presentationFiles = contents.filter(file => 
      file.path.includes('components') || 
      file.path.includes('pages') || 
      file.path.includes('views') ||
      file.path.includes('ui') ||
      file.path.includes('templates')
    );
    
    if (presentationFiles.length > 0) {
      layers.push({
        name: 'Presentation Layer',
        type: 'presentation',
        files: presentationFiles.length,
        description: 'UI components, pages, and user interface elements',
        color: '#3E90C7',
        position: 'top',
      });
    }

    // Business Logic Layer
    const businessLogicFiles = contents.filter(file => 
      file.path.includes('services') || 
      file.path.includes('business') || 
      file.path.includes('logic') ||
      file.path.includes('handlers') ||
      file.path.includes('controllers')
    );
    
    if (businessLogicFiles.length > 0) {
      layers.push({
        name: 'Business Logic Layer',
        type: 'business',
        files: businessLogicFiles.length,
        description: 'Business rules, services, and application logic',
        color: '#0F9B8E',
        position: 'middle',
      });
    }

    // Data Access Layer
    const dataAccessFiles = contents.filter(file => 
      file.path.includes('models') || 
      file.path.includes('repositories') || 
      file.path.includes('dao') ||
      file.path.includes('entities') ||
      file.path.includes('data')
    );
    
    if (dataAccessFiles.length > 0) {
      layers.push({
        name: 'Data Access Layer',
        type: 'data',
        files: dataAccessFiles.length,
        description: 'Data models, repositories, and database access',
        color: '#4E9630',
        position: 'bottom',
      });
    }

    // Infrastructure Layer
    const infrastructureFiles = contents.filter(file => 
      file.path.includes('config') || 
      file.path.includes('utils') || 
      file.path.includes('helpers') ||
      file.path.includes('middleware') ||
      file.path.includes('lib')
    );
    
    if (infrastructureFiles.length > 0) {
      layers.push({
        name: 'Infrastructure Layer',
        type: 'infrastructure',
        files: infrastructureFiles.length,
        description: 'Configuration, utilities, and cross-cutting concerns',
        color: '#BC821D',
        position: 'side',
      });
    }

    return layers;
  }

  private async analyzeModules(url: string, contents: FileAnalysis[]) {
    const modules = [];
    const modulePatterns = [
      { name: 'Authentication', patterns: ['auth', 'login', 'user', 'session'] },
      { name: 'API', patterns: ['api', 'routes', 'endpoints', 'rest'] },
      { name: 'Database', patterns: ['db', 'database', 'migration', 'schema'] },
      { name: 'Testing', patterns: ['test', 'spec', '__tests__', 'testing'] },
      { name: 'Configuration', patterns: ['config', 'settings', 'env', 'env.'] },
      { name: 'Documentation', patterns: ['docs', 'documentation', 'readme'] },
      { name: 'Build Tools', patterns: ['build', 'dist', 'webpack', 'rollup'] },
      { name: 'Assets', patterns: ['assets', 'static', 'public', 'images'] },
    ];

    for (const modulePattern of modulePatterns) {
      const moduleFiles = contents.filter(file => 
        modulePattern.patterns.some(pattern => 
          file.path.toLowerCase().includes(pattern.toLowerCase())
        )
      );

      if (moduleFiles.length > 0) {
        modules.push({
          name: modulePattern.name,
          files: moduleFiles.length,
          description: this.getModuleDescription(modulePattern.name, moduleFiles.length),
          color: this.getModuleColor(modulePattern.name),
          patterns: modulePattern.patterns,
        });
      }
    }

    return modules;
  }

  private async analyzeRelationships(url: string, contents: FileAnalysis[]) {
    const relationships = [];
    const codeFiles = contents.filter(file => 
      file.type === 'file' && 
      ['JavaScript', 'TypeScript', 'Python', 'Java'].includes(this.getLanguageFromPath(file.path))
    );

    // Analyze import/export relationships
    for (const file of codeFiles.slice(0, 10)) { // Limit for performance
      try {
        const content = await this.getFileContent(url, file.path);
        const imports = this.extractImports(content, file.language);
        
        if (imports.length > 0) {
          relationships.push({
            source: file.path,
            target: imports,
            type: 'import',
            language: file.language,
          });
        }
      } catch (error) {
        // Skip files that can't be read
      }
    }

    // Analyze architectural relationships
    const hasComponents = contents.some(file => file.path.includes('components'));
    const hasServices = contents.some(file => file.path.includes('services'));
    const hasModels = contents.some(file => file.path.includes('models'));

    if (hasComponents && hasServices) {
      relationships.push({
        source: 'Components',
        target: 'Services',
        type: 'uses',
        description: 'Components use services for business logic',
      });
    }

    if (hasServices && hasModels) {
      relationships.push({
        source: 'Services',
        target: 'Models',
        type: 'manages',
        description: 'Services manage data through models',
      });
    }

    return relationships;
  }

  private extractImports(content: string, language?: string): string[] {
    const imports: string[] = [];
    
    if (!content) return imports;

    const lines = content.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      if (language === 'JavaScript' || language === 'TypeScript') {
        // ES6 imports
        const importMatch = trimmedLine.match(/import\s+.*from\s+['"]([^'"]+)['"]/);
        if (importMatch) {
          imports.push(importMatch[1]);
        }
        
        // CommonJS requires
        const requireMatch = trimmedLine.match(/require\s*\(\s*['"]([^'"]+)['"]\s*\)/);
        if (requireMatch) {
          imports.push(requireMatch[1]);
        }
      } else if (language === 'Python') {
        // Python imports
        const importMatch = trimmedLine.match(/^(?:from\s+(\S+)\s+import|import\s+(\S+))/);
        if (importMatch) {
          imports.push(importMatch[1] || importMatch[2]);
        }
      } else if (language === 'Java') {
        // Java imports
        const importMatch = trimmedLine.match(/import\s+([^;]+);/);
        if (importMatch) {
          imports.push(importMatch[1]);
        }
      }
    }

    return imports.filter(imp => !imp.startsWith('.') && !imp.startsWith('@')); // Filter out relative imports
  }

  private getModuleDescription(moduleName: string, fileCount: number): string {
    const descriptions: { [key: string]: string } = {
      'Authentication': fileCount > 5 ? 'Comprehensive authentication system' : 'Basic authentication setup',
      'API': fileCount > 10 ? 'Extensive API with multiple endpoints' : 'Simple API structure',
      'Database': fileCount > 5 ? 'Complex database schema and migrations' : 'Basic database setup',
      'Testing': fileCount > 20 ? 'Comprehensive test coverage' : 'Basic test structure',
      'Configuration': fileCount > 3 ? 'Well-organized configuration management' : 'Simple configuration',
      'Documentation': fileCount > 5 ? 'Extensive documentation' : 'Basic documentation',
      'Build Tools': fileCount > 3 ? 'Sophisticated build system' : 'Standard build setup',
      'Assets': fileCount > 10 ? 'Rich asset management' : 'Basic asset structure',
    };
    
    return descriptions[moduleName] || 'Standard module structure';
  }

  private getModuleColor(moduleName: string): string {
    const colors: { [key: string]: string } = {
      'Authentication': '#D25B8C',
      'API': '#3E90C7',
      'Database': '#4E9630',
      'Testing': '#BC821D',
      'Configuration': '#0F9B8E',
      'Documentation': '#6FB3DE',
      'Build Tools': '#7CCA4E',
      'Assets': '#D9A05B',
    };
    
    return colors[moduleName] || '#7A837D';
  }

  private async getFilesWithContent(url: string, contents: FileAnalysis[]): Promise<FileAnalysis[]> {
    const filesWithContent: FileAnalysis[] = [];
    
    // Get content for key files that are important for security analysis
    const keyFilePatterns = [
      'package.json', 'package-lock.json', 'yarn.lock',
      '.env', '.env.example', '.env.local',
      'dockerfile', 'docker-compose',
      'requirements.txt', 'pom.xml', 'build.gradle',
      '.gitignore', '.eslintrc', '.prettierrc',
      'webpack.config', 'vite.config', 'rollup.config',
      'tsconfig.json', 'jsconfig.json',
      'README', 'CONTRIBUTING', 'LICENSE',
      'security', 'auth', 'config'
    ];

    for (const file of contents.slice(0, 50)) { // Limit to first 50 files for performance
      if (file.type === 'file') {
        try {
          // Check if this is a key file for security analysis
          const isKeyFile = keyFilePatterns.some(pattern => 
            file.path.toLowerCase().includes(pattern.toLowerCase())
          );
          
          if (isKeyFile || (file.size && file.size < 100000)) { // Only fetch content for key files or small files
            const content = await this.getFileContent(url, file.path);
            filesWithContent.push({
              ...file,
              content
            });
          } else {
            filesWithContent.push(file);
          }
        } catch (error) {
          console.warn(`Could not fetch content for ${file.path}:`, error);
          filesWithContent.push(file);
        }
      } else {
        filesWithContent.push(file);
      }
    }

    return filesWithContent;
  }

  // Helper to extract filename from path
  private getFileName(path: string): string {
    return path.split('/').pop() || path;
  }

  // Move analyzeErrors into the class
  private async analyzeErrors(url: string, contents: FileAnalysis[]) {
    const critical: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    // Check for common issues
    const hasPackageJson = contents.some(file => file.path === 'package.json');
    const hasRequirements = contents.some(file => file.path === 'requirements.txt');
    const hasDockerfile = contents.some(file => file.path.toLowerCase().includes('dockerfile'));

    if (!hasPackageJson && !hasRequirements) {
      warnings.push('No dependency management files found');
    }

    if (!hasDockerfile) {
      suggestions.push('Consider adding Docker support for easier deployment');
    }

    return { critical, warnings, suggestions };
  }

  // Move generateSummary into the class
  private generateSummary(documentation: any, architecture: any, bestPractices: any, errors: any, security: any) {
    // Calculate comprehensive overall score with weighted components
    let totalScore = 0;
    let totalWeight = 0;
    
    // Documentation Quality (20% weight)
    const docScore = documentation.documentationScore || 0;
    totalScore += docScore * 0.20;
    totalWeight += 0.20;
    
    // Code Quality & Best Practices (25% weight)
    const bestPracticesScore = bestPractices.score || 0;
    const codeQualityScore = bestPractices.codeQuality?.maintainability || 0;
    const combinedCodeScore = Math.round((bestPracticesScore + codeQualityScore) / 2);
    totalScore += combinedCodeScore * 0.25;
    totalWeight += 0.25;
    
    // Security (20% weight)
    const securityScore = security.overallScore || 0;
    totalScore += securityScore * 0.20;
    totalWeight += 0.20;
    
    // Testing Coverage (15% weight)
    const testCoverage = bestPractices.bestPractices?.testing?.coverage || 0;
    const testQuality = bestPractices.bestPractices?.testing?.hasTests ? 100 : 0;
    const testingScore = Math.round((testCoverage + testQuality) / 2);
    totalScore += testingScore * 0.15;
    totalWeight += 0.15;
    
    // CI/CD & Automation (10% weight)
    const ciScore = bestPractices.bestPractices?.cicd?.hasCI ? 100 : 0;
    const cdScore = bestPractices.bestPractices?.cicd?.hasCD ? 100 : 0;
    const cicdScore = Math.round((ciScore + cdScore) / 2);
    totalScore += cicdScore * 0.10;
    totalWeight += 0.10;
    
    // Project Structure & Organization (10% weight)
    const structureScore = bestPractices.bestPractices?.structure?.structureScore || 0;
    totalScore += structureScore * 0.10;
    totalWeight += 0.10;
    

    
    // Calculate final weighted score
    const overallScore = Math.round(totalScore / totalWeight);
    
    // Generate comprehensive strengths, weaknesses, and recommendations
    const strengths: string[] = [];
    const weaknesses: string[] = [];
    const recommendations: string[] = [];

    // Documentation analysis
    if (docScore >= 80) {
      strengths.push('Excellent documentation coverage and quality');
    } else if (docScore >= 60) {
      strengths.push('Good documentation structure with room for improvement');
    } else {
      weaknesses.push('Documentation needs significant improvement');
      recommendations.push('Add comprehensive README, contributing guidelines, and code documentation');
    }

    // Code quality analysis
    if (combinedCodeScore >= 80) {
      strengths.push('High code quality with good maintainability');
    } else if (combinedCodeScore >= 60) {
      strengths.push('Moderate code quality with some best practices');
    } else {
      weaknesses.push('Code quality and maintainability need improvement');
      recommendations.push(...bestPractices.suggestions.slice(0, 3));
    }

    // Security analysis
    if (securityScore >= 85) {
      strengths.push('Strong security posture with comprehensive measures');
    } else if (securityScore >= 70) {
      strengths.push('Good security practices with minor improvements needed');
    } else {
      weaknesses.push('Security improvements required');
      recommendations.push(...security.recommendations.slice(0, 3));
    }

    // Testing analysis
    if (testingScore >= 80) {
      strengths.push('Comprehensive testing coverage and quality');
    } else if (testingScore >= 50) {
      strengths.push('Basic testing in place with room for improvement');
    } else {
      weaknesses.push('Testing coverage and quality need improvement');
      recommendations.push('Implement comprehensive unit and integration tests');
    }

    // CI/CD analysis
    if (cicdScore >= 80) {
      strengths.push('Well-established CI/CD pipeline');
    } else if (cicdScore >= 50) {
      strengths.push('Basic automation in place');
    } else {
      weaknesses.push('CI/CD automation needs implementation');
      recommendations.push('Set up automated testing and deployment pipelines');
    }

    // Project structure analysis
    if (structureScore >= 80) {
      strengths.push('Well-organized project structure');
    } else if (structureScore >= 60) {
      strengths.push('Reasonable project organization');
    } else {
      weaknesses.push('Project structure could be improved');
      recommendations.push('Organize code into logical modules and follow standard conventions');
    }

    // Error analysis
    if (errors.critical.length === 0) {
      strengths.push('No critical issues detected');
    } else {
      weaknesses.push('Critical issues require immediate attention');
      recommendations.push(...errors.critical);
    }



    // Add AI-powered insights if available (this will be enhanced by the frontend)
    if (overallScore >= 90) {
      strengths.push('Exceptional project quality across all dimensions');
    } else if (overallScore >= 80) {
      strengths.push('High-quality project with minor improvement opportunities');
    } else if (overallScore >= 70) {
      strengths.push('Good project foundation with clear improvement path');
    } else if (overallScore >= 60) {
      strengths.push('Project has potential but needs significant improvements');
    } else {
      weaknesses.push('Project requires comprehensive improvements across multiple areas');
    }

    return {
      overallScore,
      strengths,
      weaknesses,
      recommendations,
    };
  }
}

export const githubApiService = new GitHubApiService(); 